import React from 'react';

import Grid, { GridItem } from '@marvelapp/marvel-ui/grid';
import Heading from '@marvelapp/marvel-ui/heading';
import Link from '@marvelapp/marvel-ui/link';
import List from '@marvelapp/marvel-ui/list';
import PageWrap from '@marvelapp/marvel-ui/page-wrap';
import Paragraph from '@marvelapp/marvel-ui/paragraph';
import Spacing from '@marvelapp/marvel-ui/spacing';

import Page from 'marketing-website/components/modules/Page';
import HeroSkinny from 'marketing-website/components/modules/Hero/HeroSkinny';
import metaData from 'marketing-website/data/metadata.js';

export default function Terms() {
  const boldClasses = 'fontWeight-5 c-slate';
  return (
    <Page
      title={metaData['data-processing-addendum'].title}
      meta={[
        {
          name: 'description',
          content: metaData['data-processing-addendum'].description,
        },
        {
          name: 'keywords',
          content: metaData['data-processing-addendum'].keywords,
        },
      ]}
      header
      footer
    >
      <Spacing type="margin" size="xl" position="bottom">
        <PageWrap size="s">
          <Spacing type="margin" size="xl" position="bottom">
            <HeroSkinny tag="h1" title="Data Processing Addendum" />
          </Spacing>

          <Spacing type="margin" size="m" position="bottom">
            <Paragraph tag="span" size="l">
              Marvel Prototyping Limited (“Marvel”/ “we”/ “us”/ “our”) has
              contracted to provide you (“you”, “your(s)”, “user”) with our
              design, prototyping and collaboration software as a service
              through the{' '}
              <Link
                type="blue"
                url="https://www.marvelapp.com/"
                target="_blank"
              >
                MarvelApp.com
              </Link>{' '}
              website and/or Marvel mobile application (“Services”).
            </Paragraph>
          </Spacing>

          <Spacing type="margin" size="m" position="bottom">
            <Paragraph tag="span">
              In relation to the provision of its Services to you, Marvel may
              process certain personal data of yours, in particular, Content and
              Tester Content. In order to assist you in complying with your
              obligations under the Data Protection Act 2018 and the General
              Data Protection Regulation (EU) 2016/679 (GDPR) or its successor
              or equivalent (Applicable Data Protection Law) we have set out
              below some additional terms to be incorporated into the agreement
              under which we have agreed to provide the Services to you (“Terms
              of Service”). The terms set out below shall take effect from the
              date you agree to our Terms of Service and shall replace any
              existing terms relating to the processing of your personal data.
            </Paragraph>
          </Spacing>

          <Spacing type="margin" size="m" position="bottom">
            <Paragraph tag="span">
              You acknowledge that we may process personal data provided by you
              to provide the Services. Information regarding our obligations as
              a ‘data controller’ and your rights as a data subject are set out
              in the Privacy and Cookie Policy, which are incorporated herein.
              For personal data that we process under your instructions, we are
              a ‘data processor’, and you hereby confirm that you have all
              necessary appropriate consents and notices in place to enable
              lawful transfer of such personal data to us. We will not access or
              use such personal data except as necessary to maintain or provide
              the Services, or as necessary to comply with the law or a binding
              governmental order. We will, at your cost, assist you in
              responding to any request from one of your data subjects and help
              you comply with your obligations under Applicable Data Protection
              Law with respect to security, breach notifications, impact
              assessments and consultations with supervisory authorities or
              regulators.
            </Paragraph>
          </Spacing>

          <Spacing type="margin" size="m" position="bottom">
            <Paragraph tag="span">
              [In the case of Participant Testers, where the user of Testing
              Services has not provided a privacy policy, the Participant Tester
              shall be permitted to rely on the terms of our Privacy and Cookie
              Policy, but with you, the user of Testing Services, rather than us
              being deemed the data controller, provided that the parties
              acknowledge such Privacy and Cookie Policy is supplied “as is”
              with no warranty that it is suitable for your purposes and you
              indemnity us fully with respect to any fines, damages, losses or
              costs (including legal fees) incurred by us as a result of your
              reliance on our Privacy and Cookie Policy].
            </Paragraph>
          </Spacing>

          <Spacing type="margin" size="xs" position="bottom">
            <Heading weight="semibold" size="xl">
              Data Protection Terms to be added to Terms of Service:
            </Heading>
            <Spacing type="margin" size="s" position="topBottom">
              <List type="ordered">
                <span>
                  For the purposes of these terms, “Personal Data” means data
                  about an individual who can be identified either from that
                  data or by combining the data with other information which we
                  have access to.
                </span>
                <span>
                  Both of us must comply with all Applicable Data Protection
                  Laws relating to the protection of Personal Data which apply
                  to our respective businesses.
                </span>
                <span>
                  You warrant that you have the right to transfer your Personal
                  Data to us so that we may lawfully use, process and transfer
                  it in accordance with the Terms of Service on your behalf.
                </span>
                <span>
                  To the extent you upload any content onto our Services
                  containing Personal Data and we are deemed to be a processor
                  of such Personal Data, we will:
                  <Spacing type="margin" size="s" position="topBottom">
                    <ol className="paddingLeft-m">
                      <li
                        className="marginBottom-m marginLeft-m"
                        style={{ listStyleType: 'lower-alpha' }}
                      >
                        process such Personal Data to the extent necessary in
                        order to provide our Services to you and accordance with
                        your instructions;
                      </li>
                      <li
                        className="marginBottom-m marginLeft-m"
                        style={{ listStyleType: 'lower-alpha' }}
                      >
                        take reasonable appropriate technical and organisational
                        measures against unauthorised or unlawful processing of
                        the Personal Data or its accidental loss, destruction or
                        damage as is appropriate to the harm that might result;
                      </li>
                      <li
                        className="marginBottom-m marginLeft-m"
                        style={{ listStyleType: 'lower-alpha' }}
                      >
                        ensure that anyone who has access to and/or processes
                        Personal Data is obliged to keep it confidential;
                      </li>
                      <li
                        className="marginBottom-m marginLeft-m"
                        style={{ listStyleType: 'lower-alpha' }}
                      >
                        not transfer the Personal Data outside of the European
                        Economic Area without ensuring adequate measures are in
                        place to protect the Personal Data as required by
                        applicable data protection laws;
                      </li>
                      <li
                        className="marginBottom-m marginLeft-m"
                        style={{ listStyleType: 'lower-alpha' }}
                      >
                        notify you promptly and without undue delay if we become
                        aware of a breach of security which has resulted in the
                        accidental or unlawful destruction, loss, alteration,
                        unauthorised disclosure of or access to Personal Data;
                      </li>
                      <li
                        className="marginBottom-m marginLeft-m"
                        style={{ listStyleType: 'lower-alpha' }}
                      >
                        if you ask us to and in any event on termination of the
                        Terms of Service, delete or return to you all copies of
                        the Personal Data;
                      </li>
                      <li
                        className="marginBottom-m marginLeft-m"
                        style={{ listStyleType: 'lower-alpha' }}
                      >
                        provide you with reasonable assistance and information
                        to allow you to comply with your obligations under
                        Applicable Data Protection Law;
                      </li>
                      <li
                        className="marginBottom-m marginLeft-m"
                        style={{ listStyleType: 'lower-alpha' }}
                      >
                        maintain complete and accurate records and information
                        to show we have complied with these terms; and
                      </li>
                      <li
                        className="marginBottom-m marginLeft-m"
                        style={{ listStyleType: 'lower-alpha' }}
                      >
                        permit you (or your third party auditor) to audit our
                        compliance with these terms on giving reasonable notice
                        to us, provided that any third party auditor mandated by
                        you to conduct such audit has entered into
                        confidentiality undertakings which are satisfactory to
                        us, the audit is at your expense, and you use reasonable
                        endeavours to ensure that any such audit is designed to
                        minimise disruption to our business.
                      </li>
                    </ol>
                  </Spacing>
                </span>
              </List>
            </Spacing>
          </Spacing>
          <Spacing type="margin" size="xs" position="bottom">
            <Heading weight="semibold" size="xl">
              Schedule 1
            </Heading>
            <Spacing type="margin" size="s" position="topBottom">
              <Paragraph tag="span" size="m" color="slate">
                <Grid columns={3} size="m">
                  <GridItem columnSpan={3} columnSpanM={1}>
                    <span className={boldClasses}>Scope of processing</span>
                  </GridItem>
                  <GridItem columnSpan={3} columnSpanM={2}>
                    <span>
                      Processing activities in performance of the Services under
                      the Terms of Service.
                    </span>
                  </GridItem>
                </Grid>
                <Grid columns={3} size="m">
                  <GridItem columnSpan={3} columnSpanM={1}>
                    <span className={boldClasses}>Purpose of processing</span>
                  </GridItem>
                  <GridItem columnSpan={3} columnSpanM={2}>
                    <span>
                      The provision of the Services to you, including allowing
                      you to upload content onto the Services, use the
                      interactive features of the Services, and provide Testing
                      Services involving Participant Testers.
                    </span>
                  </GridItem>
                </Grid>
                <Grid columns={3} size="m">
                  <GridItem columnSpan={3} columnSpanM={1}>
                    <span className={boldClasses}>Duration of processing</span>
                  </GridItem>
                  <GridItem columnSpan={3} columnSpanM={2}>
                    <span>
                      The duration of the provision of the Services to you.
                    </span>
                  </GridItem>
                </Grid>
                <Grid columns={3} size="m">
                  <GridItem columnSpan={3} columnSpanM={1}>
                    <span className={boldClasses}>Data Subjects</span>
                  </GridItem>
                  <GridItem columnSpan={3} columnSpanM={2}>
                    <span>
                      Individuals employed by or who work for you who access the
                      Services. Other individuals who you invite to access the
                      Services, as permitted by the Terms of Service, including
                      Third Party Users and Participant Testers.
                    </span>
                  </GridItem>
                </Grid>
                <Grid columns={3} size="m">
                  <GridItem columnSpan={3} columnSpanM={1}>
                    <span className={boldClasses}>
                      Categories of Personal Data
                    </span>
                  </GridItem>
                  <GridItem columnSpan={3} columnSpanM={2}>
                    <span>
                      You and the individuals accessing the Services (“users”)
                      control what content is uploaded onto the Services and
                      therefore what Personal Data is processed by us. This may
                      include images of the users themselves, audio and video
                      recordings, screen recordings, and any user generated
                      content (such as images, messages, posts and comments
                      attributable to the users). Users may also choose to
                      include Personal Data within the user generated content.
                    </span>
                  </GridItem>
                </Grid>
              </Paragraph>
            </Spacing>
          </Spacing>
        </PageWrap>
      </Spacing>
    </Page>
  );
}



// WEBPACK FOOTER //
// ./src/pages/data-processing-addendum/index.js